/* LuiviaRobot — Identidad visual oficial de Lluvia Studio
   SVG premium con gradientes, glow y metallic finish */

export default function LuiviaRobot({ size = 80, className = "" }) {
  return (
    <svg
      viewBox="0 0 120 130"
      width={size}
      height={Math.round(size * 130 / 120)}
      className={className}
      style={{ display: "block", flexShrink: 0 }}
      aria-label="Lluvia Studio AI"
    >
      <defs>
        {/* Face depth gradient */}
        <radialGradient id="lr-face" cx="42%" cy="38%" r="68%">
          <stop offset="0%"   stopColor="#1a3a72"/>
          <stop offset="40%"  stopColor="#0d1e42"/>
          <stop offset="100%" stopColor="#05091a"/>
        </radialGradient>

        {/* Border / ring gradient */}
        <linearGradient id="lr-ring" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%"   stopColor="#3b82f6"/>
          <stop offset="40%"  stopColor="#8b5cf6"/>
          <stop offset="100%" stopColor="#22d3ee"/>
        </linearGradient>

        {/* Eye iris gradient */}
        <radialGradient id="lr-eye" cx="50%" cy="45%" r="55%">
          <stop offset="0%"   stopColor="#e0f2fe" stopOpacity="0.95"/>
          <stop offset="25%"  stopColor="#38bdf8"/>
          <stop offset="65%"  stopColor="#2563eb"/>
          <stop offset="100%" stopColor="#1e3a8a" stopOpacity="0.9"/>
        </radialGradient>

        {/* Metallic surface highlight */}
        <linearGradient id="lr-hi" x1="20%" y1="0%" x2="70%" y2="55%">
          <stop offset="0%"   stopColor="#ffffff" stopOpacity="0.13"/>
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0"/>
        </linearGradient>

        {/* Neck gradient */}
        <linearGradient id="lr-neck" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%"   stopColor="#0d1e42"/>
          <stop offset="50%"  stopColor="#1a3060"/>
          <stop offset="100%" stopColor="#0d1e42"/>
        </linearGradient>

        {/* Eye outer socket */}
        <radialGradient id="lr-socket" cx="50%" cy="50%" r="50%">
          <stop offset="0%"   stopColor="#0a1535"/>
          <stop offset="100%" stopColor="#060c20"/>
        </radialGradient>

        {/* Antenna glow */}
        <radialGradient id="lr-ant" cx="50%" cy="50%" r="50%">
          <stop offset="0%"   stopColor="#ffffff"/>
          <stop offset="40%"  stopColor="#22d3ee"/>
          <stop offset="100%" stopColor="#0ea5e9" stopOpacity="0"/>
        </radialGradient>

        {/* Eye glow filter */}
        <filter id="lr-eglow" x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="3.5" result="b"/>
          <feColorMatrix in="b" type="matrix"
            values="0 0 0 0 0.13  0 0 0 0 0.76  0 0 0 0 0.98  0 0 0 0.7 0"
            result="c"/>
          <feMerge><feMergeNode in="c"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>

        {/* Outer glow for the whole head */}
        <filter id="lr-glow" x="-20%" y="-15%" width="140%" height="140%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="4" result="b"/>
          <feColorMatrix in="b" type="matrix"
            values="0 0 0 0 0.23  0 0 0 0 0.51  0 0 0 0 0.96  0 0 0 0.35 0"
            result="c"/>
          <feMerge><feMergeNode in="c"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>

        {/* Antenna dot glow */}
        <filter id="lr-aglow" x="-100%" y="-100%" width="300%" height="300%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="4" result="b"/>
          <feColorMatrix in="b" type="matrix"
            values="0 0 0 0 0.13  0 0 0 0 0.83  0 0 0 0 0.93  0 0 0 0.9 0"
            result="c"/>
          <feMerge><feMergeNode in="c"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
      </defs>

      {/* ── Neck ───────────────────────────────── */}
      <rect x="46" y="102" width="28" height="14" rx="5" fill="url(#lr-neck)"/>
      <rect x="46" y="102" width="28" height="14" rx="5" fill="none"
            stroke="url(#lr-ring)" strokeWidth="1" opacity="0.5"/>

      {/* ── Main head shape ─────────────────────── */}
      <rect x="10" y="22" width="100" height="82" rx="28"
            fill="url(#lr-face)" filter="url(#lr-glow)"/>

      {/* Gradient border */}
      <rect x="10" y="22" width="100" height="82" rx="28" fill="none"
            stroke="url(#lr-ring)" strokeWidth="2.5"/>

      {/* Metallic highlight */}
      <rect x="10" y="22" width="100" height="82" rx="28"
            fill="url(#lr-hi)"/>

      {/* ── Antenna stem ────────────────────────── */}
      <rect x="57.5" y="10" width="5" height="14" rx="2.5" fill="#1d4ed8"/>
      <line x1="60" y1="22" x2="60" y2="12" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round"/>

      {/* Antenna dot + glow */}
      <circle cx="60" cy="8" r="8" fill="url(#lr-ant)" filter="url(#lr-aglow)" opacity="0.6"/>
      <circle cx="60" cy="8" r="5.5" fill="#22d3ee" filter="url(#lr-aglow)"/>
      <circle cx="60" cy="8" r="3"   fill="#fff"/>

      {/* ── Eyes ────────────────────────────────── */}
      {/* Left eye socket */}
      <circle cx="40" cy="65" r="17" fill="url(#lr-socket)"/>
      {/* Left eye glow ring */}
      <circle cx="40" cy="65" r="15" fill="none"
              stroke="#22d3ee" strokeWidth="0.75" opacity="0.4" filter="url(#lr-eglow)"/>
      {/* Left iris */}
      <circle cx="40" cy="65" r="13" fill="url(#lr-eye)" filter="url(#lr-eglow)"/>
      {/* Left pupil */}
      <circle cx="40" cy="65" r="6"  fill="#0f2462"/>
      {/* Left reflection */}
      <circle cx="43"  cy="61" r="3.5" fill="#fff" opacity="0.92"/>
      <circle cx="36.5" cy="69" r="1.8" fill="#7dd3fc" opacity="0.55"/>

      {/* Right eye socket */}
      <circle cx="80" cy="65" r="17" fill="url(#lr-socket)"/>
      {/* Right eye glow ring */}
      <circle cx="80" cy="65" r="15" fill="none"
              stroke="#22d3ee" strokeWidth="0.75" opacity="0.4" filter="url(#lr-eglow)"/>
      {/* Right iris */}
      <circle cx="80" cy="65" r="13" fill="url(#lr-eye)" filter="url(#lr-eglow)"/>
      {/* Right pupil */}
      <circle cx="80" cy="65" r="6"  fill="#0f2462"/>
      {/* Right reflection */}
      <circle cx="83"  cy="61" r="3.5" fill="#fff" opacity="0.92"/>
      <circle cx="76.5" cy="69" r="1.8" fill="#7dd3fc" opacity="0.55"/>

      {/* ── Mouth ───────────────────────────────── */}
      <path d="M 44 92 Q 60 102 76 92"
            stroke="#3b82f6" strokeWidth="3" fill="none" strokeLinecap="round"/>
      <path d="M 44 92 Q 60 102 76 92"
            stroke="#93c5fd" strokeWidth="1.2" fill="none" strokeLinecap="round" opacity="0.5"/>

      {/* ── Brow accents ────────────────────────── */}
      <path d="M 29 48 Q 40 44 51 48" stroke="#3b82f6" strokeWidth="2"
            fill="none" strokeLinecap="round" opacity="0.5"/>
      <path d="M 69 48 Q 80 44 91 48" stroke="#3b82f6" strokeWidth="2"
            fill="none" strokeLinecap="round" opacity="0.5"/>

      {/* ── Tech circuit dots (subtle) ──────────── */}
      <circle cx="22" cy="38" r="1.2" fill="#3b82f6" opacity="0.3"/>
      <circle cx="30" cy="38" r="1.2" fill="#3b82f6" opacity="0.3"/>
      <line x1="22" y1="38" x2="30" y2="38" stroke="#3b82f6" strokeWidth="0.8" opacity="0.2"/>

      <circle cx="90" cy="38" r="1.2" fill="#8b5cf6" opacity="0.3"/>
      <circle cx="98" cy="38" r="1.2" fill="#8b5cf6" opacity="0.3"/>
      <line x1="90" y1="38" x2="98" y2="38" stroke="#8b5cf6" strokeWidth="0.8" opacity="0.2"/>
    </svg>
  );
}

/* RobotAvatar — robot con acento de color del agente para Mis Apps */
export function RobotAvatar({ color, size = 56 }) {
  const c = color || "#3b82f6";
  const r = Math.round(size * 0.26) + "px";
  const inner = Math.round(size * 0.82);
  return (
    <div style={{
      width: size, height: size,
      borderRadius: r,
      background: `linear-gradient(145deg, ${c}20 0%, ${c}0a 100%)`,
      border: `1.5px solid ${c}40`,
      boxShadow: `0 4px 20px ${c}28, inset 0 1px 0 rgba(255,255,255,0.06)`,
      display: "flex", alignItems: "center", justifyContent: "center",
      flexShrink: 0, position: "relative", overflow: "hidden",
    }}>
      {/* Glow halo behind robot */}
      <div style={{
        position: "absolute", inset: 0,
        background: `radial-gradient(circle at 50% 60%, ${c}15, transparent 70%)`,
      }}/>
      <LuiviaRobot size={inner} />
    </div>
  );
}

/* AIBadge — kept for backwards compat */
export function AIBadge({ color, name, size = 52 }) {
  return <RobotAvatar color={color} size={size} />;
}
